import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Sport } from '@/types';
import type { SkillLevel } from '@/components/SkillLevelBadge';

/**
 * Single source of truth for the current user's rank-related profile fields
 * (skill_level + ranked_sport). Always reads fresh from the DB so we never
 * block a user based on stale AuthContext cache after they (or an admin)
 * just updated their profile.
 *
 * Rule of thumb: any client-side gate on ranked match eligibility MUST go
 * through this hook instead of reading `AuthContext.profile.skill_level`
 * or `AuthContext.profile.ranked_sport` directly.
 */
export function useMyRankProfile() {
  const { profile } = useAuth();
  const id = profile?.id;

  const query = useQuery({
    queryKey: ['my-rank-profile', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('skill_level, ranked_sport')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return {
        skillLevel: (data?.skill_level ?? null) as SkillLevel | null,
        rankedSport: (data?.ranked_sport ?? null) as Sport | null,
      };
    },
    enabled: !!id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  return {
    skillLevel: query.data?.skillLevel ?? null,
    rankedSport: query.data?.rankedSport ?? null,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
  };
}
